"use client"

import { useState, useEffect, useCallback, useMemo } from "react"
import { useTranslations } from "next-intl"
import {
  Loader2,
  Check,
  BookOpen,
  Book,
  BookMarked,
  Library,
  BookText,
  Package,
} from "lucide-react"
import type { LucideIcon } from "lucide-react"
import { Input } from "@/components/ui/input"
import SearchableDropdown from "@/components/ui/SearchableDropdown"
import type { Option } from "@/components/ui/SearchableDropdown"
import { COMIC_FORMATS, type ComicDraft, type ComicFormat } from "@kraahl/contract"
import type { StepProps } from "@/types/listing"
import {
  creationFlowBlockClass,
  creationFlowInputClass,
  creationFlowSectionLabelClass,
} from "@/lib/creation-flow-form-styles"
import { cn } from "@/lib/utils"

interface TaxonomyItem {
  id: string
  parentId: string | null
  type: string
  slug: string | null
  name: string
  sortOrder: number
}

function dedupeTaxonomyItems(items: TaxonomyItem[]): TaxonomyItem[] {
  const seen = new Set<string>()
  const deduped: TaxonomyItem[] = []
  for (const item of items) {
    const normalizedName = item.name.trim().replace(/\s+/g, " ").toLowerCase()
    const normalizedSlug = (item.slug ?? "").trim().toLowerCase()
    const key = `${item.type}::${normalizedName || normalizedSlug}`
    if (seen.has(key)) continue
    seen.add(key)
    deduped.push(item)
  }
  return deduped
}

/**
 * Seeded escape node (migration 20260802194900 §STEP 5) — selecting it
 * routes the long-tail into `publisherCustomText` (publisherId stays null,
 * matching the `listing_comic_details.publisher_custom_text` model).
 */
const OTHER_PUBLISHER_SLUG = "other-comic"
/** Synthetic dropdown entry — series has no seeded "Other" row per publisher. */
const CUSTOM_SERIES_OPTION = "__custom_series__"

const FORMAT_ICONS: Record<ComicFormat, LucideIcon> = {
  single_issue: BookOpen,
  tpb: Book,
  hardcover: BookMarked,
  omnibus: Library,
  graphic_novel: BookText,
  box_set: Package,
}

async function fetchTaxonomy(
  types: string,
  parentId?: string | null,
  q?: string,
): Promise<TaxonomyItem[]> {
  const params = new URLSearchParams({ type: types, limit: "200" })
  if (parentId) params.set("parent_id", parentId)
  if (q?.trim()) params.set("q", q.trim())
  const res = await fetch(`/api/taxonomy?${params.toString()}`)
  if (!res.ok) return []
  const json = await res.json()
  return Array.isArray(json.items) ? json.items : []
}

export function ComicCategoryStep({
  data,
  updateDataAction,
  onNextAction,
  onValidationChange,
  onRegisterSave,
  hideButton,
  compact,
}: StepProps) {
  const t = useTranslations("comicCreation.category")
  const tFormats = useTranslations("comicCreation.formats")
  // Generic keys shared with the card flow (loading / search strings).
  const tCard = useTranslations("cardCreation.category")
  const draft = data.comicDraft

  const [publishers, setPublishers] = useState<TaxonomyItem[]>([])
  const [series, setSeries] = useState<TaxonomyItem[]>([])

  const [selectedPublisher, setSelectedPublisher] = useState<string | undefined>(
    draft?.publisherId ?? undefined
  )
  const [publisherCustomText, setPublisherCustomText] = useState(
    draft?.publisherCustomText ?? ""
  )
  const [selectedSeries, setSelectedSeries] = useState<string | undefined>(
    draft?.seriesId ?? (draft?.seriesCustomText ? CUSTOM_SERIES_OPTION : undefined)
  )
  const [seriesCustomText, setSeriesCustomText] = useState(draft?.seriesCustomText ?? "")
  const [selectedFormat, setSelectedFormat] = useState<ComicFormat | undefined>(
    draft?.format ?? undefined
  )

  const [loading, setLoading] = useState(true)
  const [seriesLoading, setSeriesLoading] = useState(false)

  useEffect(() => {
    let cancelled = false
    fetchTaxonomy("comic_publisher").then((items) => {
      if (cancelled) return
      const sorted = items.slice().sort((a, b) => a.sortOrder - b.sortOrder)
      setPublishers(dedupeTaxonomyItems(sorted))
      setLoading(false)
    })
    return () => { cancelled = true }
  }, [])

  // Draft restore: a custom-publisher draft carries only publisherCustomText
  // (publisherId is null) — re-select the "Other" node once the list arrives
  // so the dropdown + custom input reflect the saved state.
  useEffect(() => {
    if (publishers.length === 0) return
    if (!(draft?.publisherCustomText ?? "").trim()) return
    setSelectedPublisher((prev) => {
      if (prev) return prev
      const other = publishers.find((p) => p.slug === OTHER_PUBLISHER_SLUG)
      return other?.id ?? prev
    })
  }, [publishers, draft?.publisherCustomText])

  const selectedPublisherItem = publishers.find((p) => p.id === selectedPublisher)
  const isOtherPublisher = selectedPublisherItem?.slug === OTHER_PUBLISHER_SLUG
  const isCustomSeries = selectedSeries === CUSTOM_SERIES_OPTION

  useEffect(() => {
    if (!selectedPublisher || isOtherPublisher) {
      setSeries([])
      return
    }
    let cancelled = false
    setSeriesLoading(true)
    fetchTaxonomy("comic_series", selectedPublisher).then((items) => {
      if (cancelled) return
      setSeries(dedupeTaxonomyItems(items.sort((a, b) => a.sortOrder - b.sortOrder)))
      setSeriesLoading(false)
    })
    return () => { cancelled = true }
  }, [selectedPublisher, isOtherPublisher])

  const publisherOk = isOtherPublisher
    ? publisherCustomText.trim().length > 0
    : Boolean(selectedPublisher)
  const isValid = Boolean(publisherOk && selectedFormat)

  useEffect(() => {
    onValidationChange?.(isValid)
  }, [isValid, onValidationChange])

  const saveData = useCallback(() => {
    if (!selectedPublisher || !selectedFormat) return
    const comicDraft: ComicDraft = {
      ...(draft ?? {}),
      publisherId: isOtherPublisher ? null : selectedPublisher,
      publisherCustomText: isOtherPublisher ? publisherCustomText.trim() || null : null,
      seriesId: isOtherPublisher || isCustomSeries ? null : selectedSeries ?? null,
      seriesCustomText:
        isOtherPublisher || isCustomSeries ? seriesCustomText.trim() || null : null,
      format: selectedFormat,
    }
    updateDataAction({ comicDraft })
  }, [
    draft, selectedPublisher, selectedFormat, isOtherPublisher, isCustomSeries,
    publisherCustomText, selectedSeries, seriesCustomText, updateDataAction,
  ])

  useEffect(() => {
    onRegisterSave?.(saveData)
    return () => { onRegisterSave?.(null) }
  }, [saveData, onRegisterSave])

  const handlePublisherSearch = useCallback((q: string) => {
    fetchTaxonomy("comic_publisher", undefined, q).then((items) => {
      setPublishers(dedupeTaxonomyItems(items.sort((a, b) => a.sortOrder - b.sortOrder)))
    })
  }, [])

  const handleSeriesSearch = useCallback(
    (q: string) => {
      if (!selectedPublisher) return
      setSeriesLoading(true)
      fetchTaxonomy("comic_series", selectedPublisher, q)
        .then((items) => setSeries(dedupeTaxonomyItems(items.sort((a, b) => a.sortOrder - b.sortOrder))))
        .finally(() => setSeriesLoading(false))
    },
    [selectedPublisher],
  )

  const publisherOptions: Option[] = useMemo(
    () => publishers.map((p) => ({ code: p.id, name: p.name, flag: "" })),
    [publishers]
  )
  const selectedPublisherOption: Option | null = useMemo(() => {
    if (!selectedPublisher) return null
    const p = publishers.find((x) => x.id === selectedPublisher)
    return p ? { code: p.id, name: p.name, flag: "" } : null
  }, [selectedPublisher, publishers])

  const seriesOptions: Option[] = useMemo(
    () => [
      ...series.map((s) => ({ code: s.id, name: s.name, flag: "" })),
      { code: CUSTOM_SERIES_OPTION, name: t("customSeries"), flag: "" },
    ],
    [series, t]
  )
  const selectedSeriesOption: Option | null = useMemo(() => {
    if (!selectedSeries) return null
    if (selectedSeries === CUSTOM_SERIES_OPTION) {
      return { code: CUSTOM_SERIES_OPTION, name: t("customSeries"), flag: "" }
    }
    const s = series.find((x) => x.id === selectedSeries)
    return s ? { code: s.id, name: s.name, flag: "" } : null
  }, [selectedSeries, series, t])

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 className="mb-4 h-10 w-10 animate-spin text-muted-foreground" />
        <span className="text-sm text-muted-foreground">{tCard("loading")}</span>
      </div>
    )
  }

  const sectionLabel = creationFlowSectionLabelClass
  const cardBase = creationFlowBlockClass
  const cardPadding = compact ? "p-3" : "p-4"
  const inputBase = creationFlowInputClass

  return (
    <div className={compact ? "space-y-4 px-1 pt-2" : "space-y-6 px-2 pt-4"}>
      <div className={cn("rounded-2xl", cardBase, cardPadding)}>
        <p className={cn(sectionLabel, "mb-3")}>{t("publisher")}</p>
        <SearchableDropdown
          options={publisherOptions}
          label="name"
          selectedValue={selectedPublisherOption}
          onSelect={(opt) => {
            setSelectedPublisher(opt.code)
            setSelectedSeries(undefined)
            setSeriesCustomText("")
            const item = publishers.find((p) => p.id === opt.code)
            if (item?.slug !== OTHER_PUBLISHER_SLUG) setPublisherCustomText("")
          }}
          placeholder={t("publisherPlaceholder")}
          showFlag={false}
          searchPlaceholder={tCard("search")}
          onSearchQueryChange={handlePublisherSearch}
          sheetTitle={t("publisherPlaceholder")}
        />
        {isOtherPublisher && (
          <div className="mt-3 space-y-2">
            <Input
              placeholder={t("customPublisher")}
              value={publisherCustomText}
              onChange={(e) => setPublisherCustomText(e.target.value)}
              maxLength={120}
              className={inputBase}
            />
            <Input
              placeholder={t("customSeries")}
              value={seriesCustomText}
              onChange={(e) => setSeriesCustomText(e.target.value)}
              maxLength={200}
              className={inputBase}
            />
          </div>
        )}
      </div>

      {selectedPublisher && !isOtherPublisher && (
        <div className={cn("rounded-2xl", cardBase, cardPadding)}>
          <p className={cn(sectionLabel, "mb-3")}>{t("series")}</p>
          <SearchableDropdown
            options={seriesOptions}
            label="name"
            selectedValue={selectedSeriesOption}
            onSelect={(opt) => setSelectedSeries(opt.code)}
            placeholder={t("seriesPlaceholder")}
            showFlag={false}
            searchPlaceholder={tCard("search")}
            loading={seriesLoading}
            onSearchQueryChange={handleSeriesSearch}
            sheetTitle={t("seriesPlaceholder")}
          />
          {isCustomSeries && (
            <div className="mt-3">
              <Input
                placeholder={t("customSeries")}
                value={seriesCustomText}
                onChange={(e) => setSeriesCustomText(e.target.value)}
                maxLength={200}
                className={inputBase}
              />
            </div>
          )}
        </div>
      )}

      {selectedPublisher && (
        <div className={cn("rounded-2xl", cardBase, cardPadding)}>
          <p className={cn(sectionLabel, "mb-3")}>{t("format")}</p>
          <div className="grid grid-cols-2 gap-2">
            {COMIC_FORMATS.map((fmt) => {
              const Icon = FORMAT_ICONS[fmt]
              const isSelected = selectedFormat === fmt
              return (
                <button
                  key={fmt}
                  type="button"
                  onClick={() => setSelectedFormat(fmt)}
                  className={cn(
                    "flex items-center gap-2.5 rounded-xl px-3 py-3 text-left text-sm font-medium transition-colors",
                    isSelected
                      ? "bg-sheet-handle/80 text-foreground ring-1 ring-foreground/30"
                      : "bg-muted/50 text-foreground/80 hover:bg-muted/70"
                  )}
                >
                  <Icon className="h-5 w-5 shrink-0" />
                  <span className="truncate">{tFormats(fmt)}</span>
                  {isSelected && <Check className="ml-auto h-4 w-4 shrink-0" />}
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
